import * as ExcelJS from "exceljs";

import logo from "../assets/images/yoram-logo-export.png";
import { fetchImageAsBase64 } from "./Helpers";

// Parse a single CSV line, handling quoted fields
const parseCSVLine = (line) => {
  const cells = [];
  let inQuotes = false;
  let cell = "";
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') { cell += '"'; i++; }
      else inQuotes = !inQuotes;
    } else if (ch === "," && !inQuotes) {
      cells.push(cell); cell = "";
    } else {
      cell += ch;
    }
  }
  cells.push(cell);
  return cells;
};

const getColLetter = (index) => {
  let n = index;
  let letter = "";
  while (n > 0) {
    n--;
    letter = String.fromCharCode(65 + (n % 26)) + letter;
    n = Math.floor(n / 26);
  }
  return letter;
};

/**
 * Downloads the pre-signed CSV the backend generated and saves it as a styled
 * `.xlsx` workbook. Resolves to `false` when the CSV can't be fetched or converted,
 * so callers can fall back to handing the raw CSV link to the browser.
 */
export const exportCsvLinkAsXlsx = async (csvLink, sheetName = "timesheet_report") => {
  try {
    // Plain fetch (no auth headers) to avoid invalidating the S3 pre-signed URL
    const csvFetch = await fetch(csvLink, { method: "GET", credentials: "omit" });
    if (!csvFetch.ok) {
      return false;
    }

    const csvText = await csvFetch.text();
    const lines = csvText
      .replace(/^\uFEFF/, "")
      .split(/\r?\n/)
      .filter((l) => l.trim());

    const headers = parseCSVLine(lines[0]);
    const dataRows = lines.slice(1).map((l) => parseCSVLine(l));

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(sheetName);

    const logoColIndex = headers.length + 1;

    worksheet.columns = [
      ...headers.map(() => ({ width: 20 })),
      { width: 25 }, // logo column
    ];

    const headerRow = worksheet.addRow(headers);
    headerRow.height = 25;
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "D9E1F2" },
      };
      cell.font = { bold: true };
    });

    dataRows.forEach((row) => {
      worksheet.addRow(row).height = 20;
    });

    // Logo at the last column, spanning the first 4 rows
    worksheet.mergeCells(
      `${getColLetter(logoColIndex)}1:${getColLetter(logoColIndex)}4`
    );
    const logoBase64 = await fetchImageAsBase64(logo);
    const logoId = workbook.addImage({ base64: logoBase64, extension: "png" });
    worksheet.addImage(logoId, {
      tl: { col: logoColIndex - 0.7, row: 0.5 },
      ext: { width: 130, height: 25 },
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    // Derive filename from the S3 URL (same name the backend gave the CSV)
    const csvFilename = new URL(csvLink).pathname.split("/").pop();
    a.download = csvFilename.replace(/\.csv$/i, ".xlsx");
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    return true;
  } catch {
    return false;
  }
};
